import { BehaviorSubject, catchError, debounce, debounceTime, defer, distinctUntilChanged, filter, finalize, map, merge, Observable, of, retry, shareReplay, startWith, Subject, switchMap, tap, timer } from 'rxjs';
import { DataTableError, ITableDataSource, LoadingType, NullableFilterCriteria, TableCountMechanism } from './ITableDataSource';
import { applyFilterWildCardCriteria, cleanFilters } from './table-filter-functions';
import { deepCopy, isEqual } from '../../utils/static-functions';
import { ApiErrorResponse, ArrayResponse } from 'common';
import { SortDirection } from '@angular/material/sort';
import { PagingCriteria } from './TableControls';

// eslint-disable-next-line @typescript-eslint/no-inferrable-types
const LOADING_INDICATOR_DEBOUNCE_TIME: number = 200; // Time in ms to wait before displaying the loading indicator

export abstract class TableDataSource<T, F = Record<string, any>> implements ITableDataSource<T, F> {
  /**
   * RXJS approach to setTimeout / clearTimeout to ensure the loading value
   * is only applied after LOADING_INDICATOR_DEBOUNCE_TIME millis of wait time
   */
  protected readonly loadingUpdater$: Subject<LoadingType> = new Subject<LoadingType>();

  protected _apiResponse$!: Observable<ArrayResponse<T>>;

  protected _pagingCriteria: BehaviorSubject<PagingCriteria> = new BehaviorSubject<PagingCriteria>(null!);

  protected refresh$: Subject<void> = new Subject<void>();

  protected _filterCriteria$: BehaviorSubject<F> = new BehaviorSubject<F>({} as F);

  protected readonly _error$: BehaviorSubject<DataTableError | null> = new BehaviorSubject<DataTableError | null>(null);

  /**
   * A pre-processed representation of {@link _filterCriteria$}
   *
   * This will be updated any time the {@link _filterCriteria$} are updated
   */
  protected _filterCriteriaProcessed$: BehaviorSubject<F> = new BehaviorSubject<F>({} as F);

  protected _loading$: BehaviorSubject<LoadingType> = new BehaviorSubject<LoadingType>('none');

  protected _autoRefreshInterval: number = 3;

  protected _wildCardFilterCriteria: string[] = [];

  protected _nullableFilterCriteria: NullableFilterCriteria<F> = [];

  /**
   * List of filters keys that should be converted to instances of {@link Date} when filters are restored
   */
  protected _dateTimeFilterKeys: string[] = [];

  /**
   * Used to enable the automatic removal of filter values that match the configured criteria
   *
   * Example:
   *   protected override _pruneableValueMap: Partial<Record<keyof ProjectFilter, any[]>> = {
   *     archived: [-1],
   *     hasParentTemplate: [-1]
   *   };
   *
   *   With this example, if filters are processed and the properties 'archived' or 'hasParentTemplate'
   *   have the value '-1' then they will be auto pruned from the filter criteria prior to an API call being invoked
   */
  protected _pruneableValueMap: Record<string, any[]> = {};

  protected _countMechanism: TableCountMechanism = 'DEFAULT';

  private _updateTimer: any = null;

  private _lastItemCount: number = 0;

  /**
   * When set to true, this will prevent API calls from being triggered when certain properties are updated
   *
   * This can be used to allow the updating of properties without triggering API calls
   *
   * Properties:
   *  - PagingCriteria - {@link setPaging}
   *  - Filters
   */
  private _silentModeActive: boolean = false;

  protected readonly _filterListener$: Observable<F> = this._filterCriteria$
    .pipe(
      distinctUntilChanged((prev: F, curr: F) => isEqual(prev, curr))
    );

  private readonly _pageListener$: Observable<PagingCriteria> = this._pagingCriteria
    .pipe(
      distinctUntilChanged((prev: PagingCriteria, curr: PagingCriteria) => isEqual(prev, curr))
    );

  protected constructor() {
    this.listenToLoadingUpdater();

    this.listenToFilterUpdates();

    this.trackAndTriggerAPIRequests();
  }

  get dateTimeFilterKeys(): string[] {
    return [...this._dateTimeFilterKeys];
  }

  get nullableFilterCriteria(): NullableFilterCriteria<F> {
    return [...this._nullableFilterCriteria];
  }

  get countMechanism(): TableCountMechanism {
    return this._countMechanism;
  }

  get autoRefreshInterval(): number {
    return this._autoRefreshInterval;
  }

  set autoRefreshInterval(interval: number) {
    this._autoRefreshInterval = interval;
  }

  get updateTimer(): number {
    return this._updateTimer!;
  }

  get error$(): Observable<DataTableError> {
    return this._error$.asObservable() as Observable<DataTableError>;
  }

  get filterCriteria$(): BehaviorSubject<F> {
    return this._filterCriteria$;
  }

  get filterCriteriaProcessed$(): BehaviorSubject<F> {
    return this._filterCriteriaProcessed$;
  }

  get items$(): Observable<T[]> {
    return this._apiResponse$.pipe(map((res: ArrayResponse<T>) => res?.result as T[] ?? []));
  }

  get itemCount$(): Observable<number> {
    return this._apiResponse$.pipe(map((res: ArrayResponse<T>) => res?.total ?? 0));
  }

  get loading$(): Observable<LoadingType> {
    return this._loading$.asObservable();
  }

  get loading(): LoadingType {
    return this._loading$.getValue();
  }

  get pageIndex(): number {
    return this._pagingCriteria.value?.pageIndex;
  }

  get pageSize(): number {
    return this._pagingCriteria.value?.pageSize;
  }

  get sort(): string {
    return this._pagingCriteria.value?.sort;
  }

  get sortDirection(): SortDirection {
    return this._pagingCriteria.value?.direction;
  }

  get pageIndex$(): Observable<number> {
    return this._pagingCriteria.pipe(map(pc => pc.pageIndex));
  }

  get pageSize$(): Observable<number> {
    return this._pagingCriteria.pipe(map(pc => pc.pageSize));
  }

  get sort$(): Observable<string> {
    return this._pagingCriteria.pipe(map(pc => pc.sort));
  }

  get sortDirection$(): Observable<SortDirection> {
    return this._pagingCriteria.pipe(map(pc => pc.direction));
  }

  get pagingCriteria(): PagingCriteria {
    return this._pagingCriteria.getValue();
  }

  get pagingCriteria$(): Observable<PagingCriteria> {
    return this._pagingCriteria.asObservable();
  }

  set silentModeActive(value: boolean) {
    this._silentModeActive = value;
  }

  protected get lastItemCount(): number {
    return this._lastItemCount;
  }

  /**
   * Update the {@link PagingCriteria} to the values provided
   *
   * If silently is false, then no API call will be triggered
   *
   * @param sort
   * @param direction
   * @param pageIndex
   * @param pageSize
   * @param silently
   */
  setPaging(sort: string, direction: SortDirection, pageIndex: number, pageSize: number, silently: boolean = false): void {
    if (silently) {
      this._silentModeActive = silently;
    }

    this._pagingCriteria.next({ sort, direction, pageIndex, pageSize });

    if (silently) {
      requestAnimationFrame(() => {
        this._silentModeActive = false;
      });
    }
  }

  setPageSize(pageSize: number): void {
    this._pagingCriteria.next({ ...this.pagingCriteria, pageSize });
  }

  setPageIndex(pageIndex: number): void {
    this._pagingCriteria.next({ ...this.pagingCriteria, pageIndex });
  }

  setPageSort(sort: string, direction: SortDirection): void {
    this._pagingCriteria.next({ ...this.pagingCriteria, sort, direction });
  }

  refresh(): void {
    this.refresh$.next();
  }

  /**
   * No special logic required here as this service is API driven,
   * so we will just forward the invocation onto refresh()
   */
  manualRefresh(): void {
    this.refresh();
  }

  setFilters(filters: F): void {
    this._filterCriteria$.next({ ...this.getFilters(), ...filters });
  }

  getFilters(): F {
    return deepCopy<F>(this._filterCriteria$.getValue());
  }

  clearFilters(): void {
    this._filterCriteria$.next({} as F);
  }

  resetPaging(): void {
    this.setPaging(this.sort, this.sortDirection, 0, this.pageSize);
  }

  startUpdateTimer() {
    if (!this.updateTimer) {
      this._updateTimer = setInterval(() => {
        /**
         * While auto refresh is enabled, make sure we wait for the previous API call to complete first
         * before we attempt the next one, so we don't bombard the API and throttle the database
         */
        if (this.loading === null) {
          this._loading$.next('auto');

          this.manualRefresh();
        }
      }, this._autoRefreshInterval * 1000);
    }
  }

  stopUpdateTimer(): void {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this._updateTimer = null;
    }
  }

  resetLoading(): void {
    this._loading$.next('initial');
  }

  resetFilters(filters: F): void {
    this._filterCriteria$.next({ ...filters });
  }

  reset(filters: F | any = null): void {
    const shouldTriggerRefresh: boolean = isEqual(filters, this.getFilters());

    if (!filters) {
      this.clearFilters();
    }
    else {
      this.resetFilters(filters);
    }

    this.resetLoading();

    if (shouldTriggerRefresh) {
      this.refresh();
    }
  }

  /**
   * Helper method to apply Wildcards (%) to the start and end of filter values
   * that match the keys provided
   *
   * @param wildcardKeys
   * @param filters
   */
  processFilterWildcards(wildcardKeys: string[], filters: F): void {
    applyFilterWildCardCriteria<F>(filters, wildcardKeys)
  }

  /**
   * Helper method to apply Wildcards (%) to the start and end of lists of filter values seperated by a comma
   * that match the keys provided
   *
   * @param wildcardKeys
   * @param filters
   */
  processListFilterWildcards(wildcardKeys: string[], filters: F): void {
    applyFilterWildCardCriteria<F>(filters, [], wildcardKeys)
  }

  /**
   * This method will remove keys from the {@link _wildCardFilterCriteria} array
   *
   * This will ensure those filters do not get processed for applying wildcard characters to them
   */
  removeWildCardCriteria(...args: string[]): void {
    this._wildCardFilterCriteria = this._wildCardFilterCriteria.filter((key: string) => !args.includes(key));
  }

  /**
   * Add a property to {@link this._wildCardFilterCriteria} to ensure it
   * has wild card characters applied to the filter value
   *
   * @param key
   */
  addWildCardCriteria(key: keyof F): void {
    this._wildCardFilterCriteria.push(String(key));
  }

  processFilterCriteria(filters: F): F {
    const filterCriteriaCopy: F = deepCopy<F>(filters);

    cleanFilters<F>(filterCriteriaCopy, true, true, this._pruneableValueMap, this._nullableFilterCriteria);

    return applyFilterWildCardCriteria<F>(filterCriteriaCopy, this._wildCardFilterCriteria);
  }

  protected abstract getData(pagingInfo: PagingCriteria, filters: F): Observable<ArrayResponse<T>>;

  /**
   * Listen for requested changes to the loading state indicator
   * Only after LOADING_INDICATOR_DEBOUNCE_TIME millis has elapsed will we apply the update
   * Unless we are loading subsequent results or a manual refresh is clicked,
   * then we want to indicate immediately that the UI is doing something
   */
  private listenToLoadingUpdater(): void {
    this.loadingUpdater$.pipe(
      startWith('initial' as LoadingType),
      debounce((value: LoadingType) => timer(['subsequent', null].includes(value) ? 0 : LOADING_INDICATOR_DEBOUNCE_TIME))
    )
      .subscribe((loading: LoadingType) => {
        this._loading$.next(loading);

        // Only reset the error state if the loading indicator is shown
        if (loading) {
          this.resetErrorState();
        }
      });
  }

  /**
   * This method will listen to all changes in the {@link _filterCriteria$}
   *   - The PagingCriteria will be reset
   *   - The filters will get processed and {@link _filterCriteriaProcessed$} will be updated
   *
   */
  private listenToFilterUpdates(): void {
    this._filterListener$
      .subscribe((filterCriteria: F) => {
        this.resetPaging();

        this._filterCriteriaProcessed$.next(this.processFilterCriteria(filterCriteria));
      });
  }

  /**
   * This method will listen to all changes in the {@link _pageListener$} and {@link _filterListener$}
   *
   * if {@link _silentModeActive} is true no API calls will be triggered
   *
   * A small debounce of 100ms is applied to ensure we don't double fire API calls when page or filters are changed in rapid succession
   *
   * The TableDataSource.getData() method will then be invoked passing in the processed filters
   *
   */
  private trackAndTriggerAPIRequests(): void {
    const pagingAndFilterMonitor: Observable<unknown> = merge(this._pageListener$, this._filterListener$)
      .pipe(
        filter(() => !this._silentModeActive), // Ignore changes while silentModeActive is true
        debounceTime(100) // Debounce 100 ms just to prevent the initial onslaught of emissions
      );

    this._apiResponse$ = merge(this.refresh$, pagingAndFilterMonitor)
      .pipe(
        switchMap(_ => {
          return defer(() => {
            // If the automatic refresh mechanism has triggered the refresh,
            // then we will skip assigning a new loading indicator value
            if (this._loading$.getValue() !== 'auto') {
              const loadingType: LoadingType = this._lastItemCount > 0 ? 'subsequent' : 'initial';

              this.loadingUpdater$.next(loadingType);
            }

            const processedFilters: F = this.processFilterCriteria(this._filterCriteria$.value);

            return this.getData(this._pagingCriteria.value, processedFilters)
              .pipe(
                tap((response: ArrayResponse<T>) => {
                  this._lastItemCount = response.total;

                  // reset error state in case the request was quicker than the loading debounce to ensure data is now shown
                  this.resetErrorState();
                }),
                finalize(() => {
                  this.loadingUpdater$.next('none');
                }),
                catchError((requestError: ApiErrorResponse) => {
                  const error: DataTableError = this.parseError(requestError);

                  this._error$.next(error);

                  return of<ArrayResponse<T>>({ total: 0, result: [] });
                })
              );
          });
        }),
        shareReplay(1),
        retry(1)
      );
  }

  private resetErrorState(): void {
    this._error$.next(null);
  }

  private parseError(error: ApiErrorResponse): DataTableError {
    return {
      id: null,
      icon: 'report',
      title: `Request Error`,
      message: `Oops! It seems the query has encountered a problem, and we couldn't fetch the results.`
    };
  }
}
